// 共享的门店选择器：拉取可见门店，缓存 5 分钟。
// 响应式：< md 全宽，>= md 240px
// 全局状态：跟 useGlobalStore 联动 —— 一个模块切店、其他模块也跟着变。
import { Select, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { callInternal } from '../api/internal.js';
import { useBreakpoint } from '../hooks/useBreakpoint.js';
import { useGlobalStore } from '../stores/global-store.js';

export interface StoreLite {
	store_id: string;
	store: string;
	store_code: string;
	store_phone?: string;
	phone?: string;
	mobile?: string;
	tel?: string;
}

let cache: { stores: StoreLite[]; expires: number } | null = null;
const CACHE_TTL = 5 * 60 * 1000;

export async function fetchStores(): Promise<StoreLite[]> {
	if (cache && cache.expires > Date.now()) return cache.stores;
	const res = await callInternal<StoreLite[]>({ short_name: 'base', action: '/store/visible_stores' });
	if (res.code !== 0 || !Array.isArray(res.data)) return [];
	cache = { stores: res.data, expires: Date.now() + CACHE_TTL };
	return res.data;
}

interface Props {
	value?: string;
	/** 选店回调；不传则只用全局状态 */
	onChange?: (storeId: string, store: StoreLite) => void;
	style?: React.CSSProperties;
	/** 是否同步到全局 store（默认 true）*/
	syncGlobal?: boolean;
}

export default function StoreSelector({ value, onChange, style, syncGlobal = true }: Props) {
	const { t } = useTranslation();
	const { isMobile } = useBreakpoint();
	const [stores, setStores] = useState<StoreLite[] | null>(null);

	const globalStoreId = useGlobalStore((s) => s.currentStoreId);
	const globalStore = useGlobalStore((s) => s.currentStore);
	const setGlobalStore = useGlobalStore((s) => s.setStore);

	const effectiveValue = value ?? globalStoreId ?? undefined;

	useEffect(() => {
		fetchStores().then((s) => {
			setStores(s);
			if (effectiveValue && syncGlobal && (!globalStore || globalStore.store_id !== effectiveValue)) {
				const selected = s.find((x) => x.store_id === effectiveValue);
				if (selected) setGlobalStore(selected.store_id, selected);
			}
			if (s.length > 0 && !effectiveValue) {
				// 首次加载：用全局已存的或第一个，并通知调用方
				const initial = s.find((x) => x.store_id === globalStoreId) ?? s[0];
				if (syncGlobal) setGlobalStore(initial.store_id, initial);
				if (onChange) onChange(initial.store_id, initial);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!stores) return <Spin size="small" />;

	return (
		<Select
			value={effectiveValue}
			onChange={(v) => {
				const s = stores.find((x) => x.store_id === v);
				if (!s) return;
				if (syncGlobal) setGlobalStore(s.store_id, s);
				if (onChange) onChange(v, s);
			}}
			style={{ width: isMobile ? '100%' : 240, minWidth: 0, ...style }}
			placeholder={t('common.select_store')}
			options={stores.map((s) => ({ value: s.store_id, label: s.store }))}
		/>
	);
}
